/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  Alert,
  AlertTitle,
  Divider,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { getIdTypes } from "../../services/list.service";
import { getUserRoles } from "../../services/users.service";
import { issueErrorMessage, issueWarnMessage } from "../../actions/message";
import {
  isEmptyObject,
  isEmptyString,
  isNullUndefined,
  isValidDate,
  isValidPhoneNumber,
  tryParseFloat,
  tryParseInt,
} from "../../util/util";
import Permit from "../../util/permit";
import "dayjs/locale/en-gb";
import { ResponsiveRow } from "../../constants/layout.constants";
import ModalContainerModule from "../modules/modalContainer";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

/**
 *Required props are:
 - openModal: Function to close the modal ie set showTerminateModal to false.
 - closeModal: The data for the selected user being added or viewed.
 - saveUser: Function that handles the user/customer addition.
 - searchedIdType: To preload ID search type name when adding a new user
 - searchedIdNo: To preload ID number name when adding a new user
 - selectedPerson: User data to populate form.
 - manageUsers: Boolean. To render add role if using modal to manage users.
 - customer: Boolean. To render additional credit limit component.
 - task: Edit
 */

export default function AddNewUser(props) {
  const [absoluteCreditLimit, setAbsoluteCreditLimit] = useState(0);
  const [idNoSearch, setIdNo] = useState("");
  const [idTypeSearch] = useState("");
  const [idTypes, setIdTypes] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState();
  const [selectedRole, setSelectedRole] = useState("");
  const [approveCapitalFunds, setApproveCapitalFunds] = useState(false);
  const [viewOnlyAccess, setViewOnlyAccess] = useState(false);
  const [approveBudgets, setApproveBudgets] = useState(false);
  const disabledOnEdit = !props?.allowEditUser && (
      props?.task === "Edit" ||
      (props?.task === "Add" && Boolean(props?.selectedPerson?.id))
  );

  useEffect(() => {
    if (!props.openModal) return;
    getIdTypes().then((idTypes) => setIdTypes(idTypes));
    getUserRoles().then((userRoles) => setUserRoles(userRoles));
  }, [props.openModal]);

  useEffect(() => {
    if (!props.openModal) return;

    if (props.task === "Edit") {
      if (isNullUndefined(props.selectedPerson)) return;

      const person = { ...props.selectedPerson };
      setSelectedUser(person);

      if (person.permissions) {
        setViewOnlyAccess(person.permissions.includes("view only access"));
        setApproveCapitalFunds(person.permissions.includes("capital fund"));
        setApproveBudgets(person.permissions.includes("budget"));
      }
    } else {
      // Non-Edit task (Create)
      const data = {
        id: null,
        firstName: "",
        middleName: "",
        lastName: "",
        mobilePhone: "",
        officePhone: "",
        email: "",
        credentialId: "",
        idNumber: props.searchedIdNo ?? "",
        idTypeCd: props.searchedIdType ?? "",
        photoFile: "",
        name: "",
        organization: null,
        username: "",
        roleCode: "",
        roleName: "",
        permissions: [],
        absoluteCreditLimit: "",
        billingPeriod: "",
        billingDate: "",
        billingDay: "",
        type: "Individual",
      };

      if (isEmptyObject(props.selectedPerson)) {
        setSelectedUser(data);
      } else {
        setSelectedUser({ ...data, ...props.selectedPerson });
      }
    }
  }, [
    props.openModal,
    props.task,
    props.selectedPerson,
    props.searchedIdNo,
    props.searchedIdType
  ]);

  const handleRoleChange = (event) => {
    let value = event.target.value;
    setSelectedRole(value);
    if (isNullUndefined(selectedUser)) return;
    selectedUser.roleCode = value;
  };

  function handleIdTypeChange(event) {
    let value = event.target.value;
    setSelectedRole(value);
    if (isNullUndefined(selectedUser)) return;
    selectedUser.idTypeCd = value;
  }

  function handleSaveUserClicked() {
    if (validateForm()) {
      const saving = props.saveUser(selectedUser);
      // Parents that save on the server hand back a promise: wait for it, and
      // keep this form open when the save is rejected (e.g. the contact's
      // email or phone is not available). Parents that only make a selection
      // return nothing, so there is nothing to wait for.
      if (!isNullUndefined(saving) && typeof saving.then === "function") {
        saving.then((response) => {
          if (isNullUndefined(response) || response.type === "error") return;
          closeModal();
        });
      } else {
        closeModal();
      }
    }
  }

  function handleInputChange(e) {
    if (isNullUndefined(selectedUser)) return;

    switch (e.target.name) {
      case "firstName":
        setSelectedUser((prev) => ({...prev, firstName: e.target.value}));
        break;

      case "middleName":
        setSelectedUser((prev) => ({...prev, middleName: e.target.value}));
        break;

      case "lastName":
        setSelectedUser((prev) => ({...prev, lastName: e.target.value}));
        break;

      case "mobilePhone":
        setSelectedUser((prev) => ({...prev, mobilePhone: e.target.value}));
        break;

      case "idNo":
        setIdNo(e.target.value);
        setSelectedUser((prev) => ({...prev, idNumber: e.target.value}));
        break;

      case "officePhone":
        setSelectedUser((prev) => ({...prev, officePhone: e.target.value}));
        break;

      case "email":
        setSelectedUser((prev) => ({...prev, email: e.target.value}));
        break;

      case "absoluteCreditLimit":
        setAbsoluteCreditLimit(e.target.value);
        setSelectedUser((prev) => ({...prev, absoluteCreditLimit: e.target.value}));
        break;

      default:
        break;
    }
  }

  function validateForm() {
    let sendData = selectedUser;
    if (isNullUndefined(sendData)) {
      issueErrorMessage(
        "Could not find user data, please refresh the page and try again.",
      );
      return false;
    }
    if (isEmptyString(sendData.firstName) || isEmptyString(sendData.lastName)) {
      issueWarnMessage("First name and Last name are required.");
      return false;
    }
    sendData.name = sendData.firstName.trim().concat(" ").concat(sendData.lastName.trim());
    if (
      isNullUndefined(sendData.mobilePhone) ||
      isEmptyString(sendData.mobilePhone)
    ) {
      issueWarnMessage("Mobile Phone number is required.");
      return false;
    }
    const { isValid, errors } = isValidPhoneNumber(sendData?.mobilePhone);
    if (isValid === false) {
      issueWarnMessage(`Mobile phone number is invalid. ${errors.join(". ")}`);
      return false;
    }

    if (sendData?.officePhone) {
      if (sendData?.officePhone === sendData?.mobilePhone) {
        issueWarnMessage(
          `Office phone number cannot be same as mobile phone number. Leave blank if you don't have a separate phone number.`,
        );
        return false;
      }
      const { isValid, errors } = isValidPhoneNumber(sendData?.officePhone);
      if (!isValid) {
        issueWarnMessage(
          `Office phone number is invalid. ${errors.join(". ")}`,
        );
        return false;
      }
    }
    if (
      isNullUndefined(sendData.idNumber) ||
      isEmptyString(sendData.idNumber) ||
      isNullUndefined(sendData.idTypeCd) ||
      isEmptyString(sendData.idTypeCd)
    ) {
      issueWarnMessage("Must provide and ID number and ID type.");
      return false;
    }
    if (props.manageUsers) {
      if (isNullUndefined(sendData.email) || isEmptyString(sendData.email)) {
        issueWarnMessage("Must provide an email.");
        return false;
      }

      if (
        isNullUndefined(sendData.roleCode) ||
        isEmptyString(sendData.roleCode)
      ) {
        issueWarnMessage("Must select a role to assign to this user.");
        return false;
      }
    }

    if (props.customer) {
      let creditLimit = tryParseFloat(sendData.absoluteCreditLimit, -1);

      if (creditLimit < 0) {
        issueWarnMessage("Must provide a valid credit Limit.");
        return false;
      }
      sendData.absoluteCreditLimit = creditLimit;

      if (sendData.billingPeriod === "MNTH") {
        if (
          isNullUndefined(sendData.billingDay) ||
          isEmptyString(sendData.billingDay)
        ) {
          issueWarnMessage("Must provide a billing day.")
          return false
        }
        let billingDay = tryParseInt(sendData.billingDay, -1)
        if (billingDay < 0) {
          issueWarnMessage("Must provide a valid billing day.")
          return false
        }
        sendData.billingDay = billingDay
      }

      if (sendData.billingPeriod === "QT" || sendData.billingPeriod === "ANU") {
        if (
          isNullUndefined(sendData.billingDate) ||
          isEmptyString(sendData.billingDate)
        ) {
          issueWarnMessage("Must provide a billing date.")
          return false
        }
        let date = new Date(sendData.billingDate)
        if (!isValidDate(date)) {
          issueWarnMessage("Must provide a valid date.")
          return false
        }
        sendData.billingDate = date
      }
    }
    setSelectedUser(sendData);
    return true;
  }

  // function handleNextBillingDateChange(value) {
  //   let date = new Date(value)
  //   let obj = selectedUser
  //   obj.billingDate = date
  //   setSelectedUser(obj)
  //   setBillingDate(date)
  // }
  //
  // function handleBillingPeriodChange(event) {
  //   let value = event.target.value
  //   let obj = selectedUser
  //   obj.billingPeriod = value
  //   setSelectedUser(obj)
  // }
  //
  // function handleBillingDayChange(event) {
  //   let value = event.target.value
  //   let obj = selectedUser
  //   obj.billingDay = value
  //   setSelectedUser(obj)
  // }

  function closeModal() {
    props.closeModal();
    setSelectedUser(null);
    setSelectedRole("");
    setIdNo("");
  }

  function handleSwitchToggleChanged(event, permission) {
    if (isNullUndefined(selectedUser)) return;

    let obj = selectedUser;
    // A person loaded for Edit may arrive without a permissions array.
    if (isNullUndefined(obj.permissions)) obj.permissions = [];
    if (permission === "capital fund") {
      if (event.target.checked) {
        obj.permissions.push(permission);
        setApproveCapitalFunds(true);
      } else {
        obj.permissions.splice(obj.permissions.indexOf("capital fund"));
        setApproveCapitalFunds(false);
      }
    } else if (permission === "budget") {
      if (event.target.checked) {
        obj.permissions.push(permission);
        setApproveBudgets(true);
      } else {
        obj.permissions.splice(obj.permissions.indexOf("budget"));
        setApproveBudgets(false);
      }
    } else if (permission === "view only access") {
      if (event.target.checked) {
        obj.permissions.push(permission);
        setViewOnlyAccess(true);
      } else {
        obj.permissions.splice(obj.permissions.indexOf("view only access"));
        setViewOnlyAccess(false);
      }
    }
    setSelectedUser(obj);
  }

  let modalTitle =
    !isNullUndefined(props.task) && props.task === "Edit"
      ? "Edit User"
      : "Add User";
  let acceptButtonText =
    !isNullUndefined(props.task) && props.task === "Edit"
      ? "Update"
      : "Create User";
  let headingText = "User's Details";
  if (props.customer) {
    modalTitle =
      !isNullUndefined(props.task) && props.task === "Edit"
        ? "Edit Customer"
        : "Add Customer";
    acceptButtonText =
      !isNullUndefined(props.task) && props.task === "Edit"
        ? "Update"
        : "Create Customer";
    headingText = "Customer's Details";
  }

  return (
    <>
      <ModalContainerModule
        size="specialAddNewUser"
        accept={handleSaveUserClicked}
        cancel={() => closeModal()}
        openModal={props.openModal}
        modalTitle={modalTitle}
        acceptButtonText={acceptButtonText}
      >
        {props.task === "Edit" && (
          <Alert severity="info" sx={{ width: "100%", marginBottom: "15px" }}>
            <AlertTitle>
              Note: You are currently editing the details of an existing user.
            </AlertTitle>
            This form only updates the details of the current contact. To perform a different action, please contact support.
          </Alert>
        )}
        <Typography
          id="addUser"
          variant="h5"
          component="h6"
          sx={{ marginBottom: "20px" }}
        >
          {headingText}
        </Typography>
        <ResponsiveRow
          container
          sx={{ height: "auto", margin: 0, paddingTop: 0 }}
        >
          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={disabledOnEdit}
          >
            <InputLabel required>First Name</InputLabel>
            <OutlinedInput
              label="First Name"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"firstName"}
              onChange={handleInputChange}
              value={selectedUser?.firstName ?? ""}
              inputProp={(params) => <TextField {...params} />}
            />
          </FormControl>
          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={disabledOnEdit}
          >
            <InputLabel>Middle Name</InputLabel>
            <OutlinedInput
              label="Middle Name"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"middleName"}
              onChange={handleInputChange}
              value={selectedUser?.middleName ?? ""}
            />
          </FormControl>
          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={disabledOnEdit}
          >
            <InputLabel required>Last Name</InputLabel>
            <OutlinedInput
              label="Last Name"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"lastName"}
              onChange={handleInputChange}
              value={selectedUser?.lastName ?? ""}
            />
          </FormControl>

          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={disabledOnEdit}
          >
            <InputLabel required>Mobile Phone (Primary No.)</InputLabel>
            <OutlinedInput
              label="Mobile Phone (Primary No.)"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"mobilePhone"}
              onChange={handleInputChange}
              value={selectedUser?.mobilePhone ?? ""}
            />
          </FormControl>

          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={true}
          >
            <InputLabel required>ID Type</InputLabel>
            <Select
              label="ID Type"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"idType"}
              required={true}
              value={
                selectedUser
                  ? selectedUser && selectedUser.idTypeCd
                  : idTypeSearch
              }
              onChange={handleIdTypeChange}
            >
              {idTypes && idTypes.length > 0 ? (
                idTypes.map(function (idType, i) {
                  return (
                    <MenuItem key={idType.value} value={idType.value}>
                      {idType.label}
                    </MenuItem>
                  );
                }, this)
              ) : (
                <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={true}
          >
            <InputLabel required>ID Number</InputLabel>
            <OutlinedInput
              label="ID Number"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"idNo"}
              readOnly={props.task === "Edit"}
              onChange={handleInputChange}
              value={
                selectedUser
                  ? selectedUser && selectedUser.idNumber
                  : idNoSearch
              }
            />
          </FormControl>

          <FormControl
            item
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={disabledOnEdit}
          >
            <InputLabel>Office Phone (Secondary No.)</InputLabel>
            <OutlinedInput
              label="Office Phone (Secondary No.)"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"officePhone"}
              onChange={handleInputChange}
              value={selectedUser?.officePhone ?? ""}
            />
          </FormControl>

          <FormControl
            item
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
            disabled={disabledOnEdit}
          >
            <InputLabel>Email</InputLabel>
            <OutlinedInput
              label="Email"
              sx={{ width: { xs: "100%", md: "90%" } }}
              name={"email"}
              onChange={handleInputChange}
              value={selectedUser?.email ?? ""}
            />
          </FormControl>
        </ResponsiveRow>
        <br />
        <Divider /> <br />
        {selectedUser &&
          !isNullUndefined(selectedUser.roleCode) &&
          selectedUser.roleCode === "OWN" && (
            <ResponsiveRow
              sx={{
                flexDirection: "column",
                width: "100%",
                height: "auto",
              }}
            >
              <Permit roles="BLDIR,BLDMG,REC,CS" services="HOA">
                <Typography
                  id="permissions"
                  variant="h6"
                  component="h6"
                  sx={{ marginBottom: "10px" }}
                >
                  Activate Permissions at this Property.
                </Typography>
              </Permit>
              <Alert severity="info">
                Activating permission will make an owner a committee member for
                respective granted permission. That is
                <span
                  style={{
                    padding: 5,
                    fontWeight: "bold",
                  }}
                >
                  Capital Fund Committee
                </span>
                or
                <span
                  style={{
                    padding: 5,
                    fontWeight: "bold",
                  }}
                >
                  Budget Committee
                </span>
                or
                <span
                  style={{
                    padding: 5,
                    fontWeight: "bold",
                  }}
                >
                  Both.
                </span>
              </Alert>
              <br />

              <ResponsiveRow container sx={{ height: "auto" }}>
                {/*<Grid item sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: '10px' }}>*/}
                {/*    <InputLabel>View Only Access</InputLabel>*/}
                {/*    <Switch*/}
                {/*        checked={viewOnlyAccess}*/}
                {/*        onChange={(event) => handleSwitchToggleChanged(event, "view only access")}*/}
                {/*        inputProps={{'aria-label': 'controlled'}}*/}
                {/*    />*/}
                {/*</Grid>*/}

                <FormControl
                  item
                  component={FormControl}
                  sx={{
                    width: { xs: "100%", lg: "33.3%" },
                    marginBottom: "10px",
                  }}
                >
                  <FormLabel>Is a Capital Fund Committee Member</FormLabel>
                  <Switch
                    checked={approveCapitalFunds}
                    onChange={(event) =>
                      handleSwitchToggleChanged(event, "capital fund")
                    }
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </FormControl>

                <FormControl
                  item
                  component={FormControl}
                  sx={{
                    width: { xs: "100%", lg: "33.3%" },
                    marginBottom: "10px",
                  }}
                >
                  <FormLabel>Is a Budget Committee Member</FormLabel>
                  <Switch
                    checked={approveBudgets}
                    onChange={(event) =>
                      handleSwitchToggleChanged(event, "budget")
                    }
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </FormControl>
              </ResponsiveRow>
            </ResponsiveRow>
          )}
        {props.manageUsers && (
          <Typography id="selectRole" variant="h6" component="h6">
            Add Role
          </Typography>
        )}
        {props.manageUsers && (
          <Grid
            item
            component={FormControl}
            sx={{
              width: { xs: "100%", lg: "33.3%" },
              marginBottom: "10px",
            }}
          >
            <InputLabel>Role at this property</InputLabel>
            <Select
              defaultValue=""
              id="filterUser"
              value={
                selectedUser
                  ? selectedUser && selectedUser.roleCode
                  : selectedRole
              }
              sx={{
                width: { lg: "250px", md: "175px", xs: "100%" },
                height: 50,
              }}
              onChange={handleRoleChange}
            >
              {userRoles && userRoles.length > 0 ? (
                userRoles.map(function (role, i) {
                  return (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  );
                }, this)
              ) : (
                <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
              )}
            </Select>
          </Grid>
        )}
        {props.customer && (
          <Typography id="absoluteCreditLimit" variant="h6" component="h6">
            Add Credit Limit
          </Typography>
        )}
        {props.customer && (
          <ResponsiveRow container sx={{ height: "auto" }}>
            <FormControl
              item
              sx={{
                width: { xs: "100%", lg: "33.3%" },
                marginBottom: "10px",
              }}
            >
              <InputLabel required>Credit Limit</InputLabel>
              <OutlinedInput
                label="Credit Limit"
                sx={{ width: { xs: "100%", md: "90%" } }}
                name={"absoluteCreditLimit"}
                onChange={handleInputChange}
                value={
                  selectedUser
                    ? selectedUser && selectedUser.absoluteCreditLimit
                    : absoluteCreditLimit
                }
              />
            </FormControl>
            <FormControl
              item
              sx={{
                width: { xs: "100%", lg: "33.3%" },
                marginBottom: "20px",
              }}
            >
              <InputLabel required>Billing Frequency</InputLabel>
              <Select
                label="Billing Frequency"
                sx={{ width: { xs: "100%", md: "90%" } }}
                id={"billingPeriod"}
                value={selectedUser?.billingPeriod || ""}
                onChange={(event) => {
                  let billingPeriod = event.target.value;
                  let obj = { ...selectedUser };
                  obj.billingPeriod = billingPeriod;
                  setSelectedUser(obj);
                }}
              >
                {props.billingPeriods && props.billingPeriods.length > 0 ? (
                  props.billingPeriods.map(function (billingPeriod, i) {
                    return (
                      <MenuItem key={i} value={billingPeriod.value}>
                        {billingPeriod.label}
                      </MenuItem>
                    );
                  }, this)
                ) : (
                  <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                )}
              </Select>
            </FormControl>

            {selectedUser?.billingPeriod === "MNTH" && (
              <FormControl
                item
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
              >
                <InputLabel required>Billing Due Day</InputLabel>
                <Select
                  label={"Billing Due Day"}
                  sx={{ width: { xs: "100%", lg: "90%" } }}
                  id={"billingDay"}
                  value={selectedUser?.billingDay}
                  onChange={(e) => {
                    let billingDay = e.target.value;
                    let obj = { ...selectedUser };
                    obj.billingDay = billingDay;
                    setSelectedUser(obj);
                  }}
                  defaultValue={[""]}
                >
                  {props?.meterBillingDays && props?.meterBillingDays.length > 0 ? (
                    props?.meterBillingDays.map(function (weekDay, i) {
                      return (
                        <MenuItem key={i} value={weekDay.value}>
                          {weekDay.label}
                        </MenuItem>
                      );
                    }, this)
                  ) : (
                    <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            {selectedUser?.billingPeriod &&
              (selectedUser?.billingPeriod === "ANU" || selectedUser?.billingPeriod === "QT") && (
                <FormControl
                  item
                  sx={{
                    width: { xs: "100%", lg: "33.3%" },
                    marginBottom: "10px",
                  }}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="en-gb"
                  >
                    <DatePicker
                      label={"Next Rent Due Date"}
                      sx={{ width: { xs: "100%", lg: "90%" } }}
                      id={"nextRentDate"}
                      value={dayjs(selectedUser?.billingDate)}
                      onChange={(event) => {
                        let value = event.target.value;
                        let date = new Date(value);
                        let obj = { ...selectedUser };
                        obj.billingDate = date;
                        setSelectedUser(obj);
                      }}
                      disablePast
                    />
                  </LocalizationProvider>
                </FormControl>
              )}
          </ResponsiveRow>
        )}
      </ModalContainerModule>
    </>
  );
}
